/**
 * @author: Symmetry Commerce (symmetrycommerce.com)
 * @version: 1.0.0
 * @description: Linked Products Web Component
 * @description: Displays related products in a dropdown format.
 */

class LinkedProducts extends HTMLElement {
  constructor() {
    super();
    this.config = {
      apiUrl: "/api/2025-10/graphql.json",
      apiToken: this.dataset.apiToken || "",
      maxProducts: 100
    };

    this.productQuery = `
      query ParentGroupProducts($query: String!) {
        products(first: ${this.config.maxProducts}, query: $query) {
          nodes {
            id
            title
            handle
            featuredImage {
              url
              altText
            }
            tags
            dropDownValue: metafield(namespace: "custom", key: "dropdown_value") {
              value
            }
            dropDownTitle: metafield(namespace: "custom", key: "dropdown_title") {
              value
            }
          }
        }
      }`;
  }

  connectedCallback() {
    this.init();
  }

  async init() {
    const parentGroup = this.dataset.tag;
    const currentHandle = this.dataset.currentHandle;
    const heading = this.dataset.heading;

    if (!parentGroup) {
      console.log('[LinkedProducts] No parent group found');
      this.style.display = 'none';
      return;
    }

    console.log('[LinkedProducts] Initializing with tag:', parentGroup);

    try {
      const products = await this.fetchLinkedProducts(parentGroup);
      const currentProduct = products.find(p => p.handle === currentHandle);
      const filteredProducts = products.filter(p => p.handle !== currentHandle);

      if (!filteredProducts.length) {
        console.log('[LinkedProducts] No filtered products found');
        this.style.display = 'none';
        return;
      }

      this.renderProducts(heading, filteredProducts, currentProduct);
    } catch (error) {
      console.error('[LinkedProducts] Error:', error);
      this.style.display = 'none';
      this.dispatchEvent(new CustomEvent('linked-products-error', { 
        detail: { error },
        bubbles: true 
      }));
    }
  }

  async fetchLinkedProducts(tag) {
    const response = await fetch(this.config.apiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": this.config.apiToken
      },
      body: JSON.stringify({
        query: this.productQuery,
        variables: { query: `tag:'${tag}'` }
      })
    });

    const json = await response.json();
    console.log('[LinkedProducts] Fetched products:', json);
    return json?.data?.products?.nodes || [];
  }

  buildDropDownsMap(products) {
    const dropDownsMap = new Map();

    products.forEach(product => {
      if (!product.dropDownTitle?.value || !product.dropDownValue?.value) {
        return;
      }

      const key = product.dropDownTitle.value;
      const productId = product.id.split('/');
      
      const data = {
        id: productId[productId.length - 1],
        value: product.dropDownValue.value,
        link: `/products/${product.handle}`,
        title: product.title
      };

      dropDownsMap.set(key, [...(dropDownsMap.get(key) || []), data]);
    });

    return dropDownsMap;
  }

  createDropDowns(dropDownsMap, currentProduct) {
    const container = document.createElement('div');
    container.className = 'product-dropdowns-container';
    container.setAttribute('role', 'group');
    container.setAttribute('aria-label', 'Product variant selectors');

    dropDownsMap.forEach((data, key) => {
      const wrapper = this.createDropdownWrapper(key, data, currentProduct);
      container.appendChild(wrapper);
    });

    return container;
  }

  createDropdownWrapper(key, products, currentProduct) {
    const wrapper = document.createElement('div');
    wrapper.className = 'product-dropdown-wrapper';

    const selectId = `product-dropdown-${key.replace(/\s+/g, '-').toLowerCase()}`;
    
    // Create label
    const label = document.createElement('label');
    label.className = 'product-dropdown-label';
    label.textContent = key;
    label.setAttribute('for', selectId);

    // Create select
    const select = document.createElement('select');
    select.className = 'product-dropdown-select';
    select.setAttribute('aria-label', `Select ${key}`);
    select.setAttribute('name', `product-dropdown-${key}`);
    select.setAttribute('id', selectId);

    // Find current product's value for this dropdown
    const currentValue = currentProduct?.dropDownTitle?.value === key 
      ? currentProduct.dropDownValue?.value 
      : null;

    // Add default option showing current value
    const defaultOption = document.createElement('option');
    const defaultOptionId = window.ShopifyAnalytics?.meta?.product?.id;

    defaultOption.setAttribute('value', '');
    defaultOption.setAttribute('data-product-id', defaultOptionId);
    defaultOption.textContent = currentValue || `Select ${key}...`;
    defaultOption.disabled = false;
    defaultOption.selected = true;
    select.appendChild(defaultOption);

    // Add product options
    products.forEach(product => {
      const option = document.createElement('option');
      option.setAttribute('value', product.value);
      option.setAttribute('data-product-id', product.id);
      option.setAttribute('data-product-url', product.link);
      option.setAttribute('data-product-title', product.title);
      option.textContent = product.value;
      select.appendChild(option);
    });

    // Add event listener for redirect
    select.addEventListener('change', (e) => this.handleSelectChange(e));

    // Add keyboard navigation hint
    const hint = document.createElement('span');
    hint.className = 'sr-only';
    hint.textContent = 'Use arrow keys to navigate options, Enter to select';
    
    wrapper.appendChild(label);
    wrapper.appendChild(select);
    wrapper.appendChild(hint);

    return wrapper;
  }

  handleSelectChange(event) {
    const select = event.target;
    const selectedOption = select.options[select.selectedIndex];
    const productId = selectedOption.getAttribute('data-product-id');
    const productUrl = selectedOption.getAttribute('data-product-url');
    const productTitle = selectedOption.getAttribute('data-product-title');

    if (productUrl) {
      // Dispatch custom event before navigation
      this.dispatchEvent(new CustomEvent('linked-product-selected', {
        detail: { 
          id: productId,
          url: productUrl, 
          title: productTitle,
          value: selectedOption.value 
        },
        bubbles: true
      }));

      // Navigate to product
      window.location.href = productUrl;
    }
  }

  renderProducts(heading, products, currentProduct) {
    const dropDownsMap = this.buildDropDownsMap(products);
    const dropDownsContainer = this.createDropDowns(dropDownsMap, currentProduct);

    // Clear loading state
    this.innerHTML = '';
    this.appendChild(dropDownsContainer);
    this.classList.add('loaded');

    // Add accessibility announcement
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.className = 'sr-only';
    announcement.textContent = `${products.length} linked ${products.length === 1 ? 'product' : 'products'} available`;
    this.appendChild(announcement);

    // Dispatch loaded event
    this.dispatchEvent(new CustomEvent('linked-products-loaded', {
      detail: { productCount: products.length },
      bubbles: true
    }));
  }
}

// Register the custom element
if (!customElements.get('linked-products')) {
  customElements.define('linked-products', LinkedProducts);
}
